import {
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Trans, useTranslation } from 'react-i18next';
import { GeoEventMetadataText } from 'features/geoEvents/components/GeoEventMetadataRows';
import { getResponsiveFontSize } from 'theme/watchdutyTheme.utils';
import { getDateFormatted, getTimeAgo } from 'shared/dates';
import { parseEvacZoneName } from 'features/mapping/mapping.utils';
import { EvacZonePoi } from './PoiDialog.types';
import {
  EVAC_ZONE_LABEL_TRANS_KEYS,
  EVAC_ZONE_LAYER_COLORS,
  EMPTY_DATA_VALUE,
  EvacZoneStyles,
} from '../../../../../constants';

const useStyles = makeStyles()((theme) => ({
  title: {
    padding: theme.spacing(2, 6, 0, 2),
    lineHeight: '1.2 !important',
    display: 'flex',
    flexDirection: 'column',
  },
  zoneTitle: {
    textTransform: 'uppercase',
    fontWeight: theme.typography.fontWeightBold,
    fontSize: getResponsiveFontSize('0.625rem'),
  },
  content: {
    padding: theme.spacing(0, 2, 1),
  },
  info: {
    padding: theme.spacing(0, 2, 1.5),
  },
  icon: {
    width: 12,
    height: 12,
    borderRadius: 12,
  },
}));

export const EvacZoneDialogContent = (props: EvacZonePoi): JSX.Element => {
  const { status, style, displayName, dateModified, source, dateCreated } =
    props;
  const { classes } = useStyles();
  const { t } = useTranslation();

  const isActiveZone: boolean = Boolean(status);
  const evacZoneStyle = style || EvacZoneStyles.default;

  return (
    <>
      <DialogTitle id="alert-dialog-title" className={classes.title}>
        <Typography
          component="span"
          variant="body2"
          sx={{
            fontWeight: 'bold',
          }}
        >
          {t('map.poi.evacZone.title', {
            displayName: parseEvacZoneName(displayName),
          })}
        </Typography>
      </DialogTitle>

      {status && (
        <DialogContent className={classes.content}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            <div
              className={classes.icon}
              style={{
                backgroundColor: EVAC_ZONE_LAYER_COLORS[evacZoneStyle][status],
              }}
            />

            <DialogContentText
              id="alert-dialog-description"
              color="textPrimary"
              variant="body2"
              fontWeight="bold"
            >
              {t(EVAC_ZONE_LABEL_TRANS_KEYS[evacZoneStyle][status])}
            </DialogContentText>
          </Stack>
        </DialogContent>
      )}

      <DialogContent className={classes.info}>
        {status && dateModified && (
          <GeoEventMetadataText>
            <Trans
              i18nKey="map.poi.evacZone.updated"
              values={{
                timeAgo: dateModified
                  ? getTimeAgo(dateModified)
                  : EMPTY_DATA_VALUE,
              }}
            >
              Updated <b>5 minutes ago</b>
            </Trans>
          </GeoEventMetadataText>
        )}

        {isActiveZone ? (
          <GeoEventMetadataText>
            <Trans
              i18nKey="map.poi.evacZone.county"
              values={{
                county: source,
                timeAgo: dateCreated
                  ? getTimeAgo(dateCreated)
                  : EMPTY_DATA_VALUE,
                formattedDate: dateCreated
                  ? getDateFormatted(dateCreated)
                  : EMPTY_DATA_VALUE,
              }}
            >
              <b>El Dorado County</b> • 5 days ago • Aug 4, 12:54 PM
            </Trans>
          </GeoEventMetadataText>
        ) : (
          <GeoEventMetadataText fontWeight="fontWeightBold">
            {source}
          </GeoEventMetadataText>
        )}
      </DialogContent>
    </>
  );
};
